import { Breadcrumbs } from '@innovationnorway/cl-react';
import React, { createContext, PropsWithChildren, useContext } from 'react';
import { CustomTypeOptions, useTranslation } from 'react-i18next';
import { Link, matchRoutes, RouteObject, useLocation } from 'react-router-dom';

// Labels align with the route's own path segments; ':name' shows that route param, anything else is an i18n key.
type CrumbHandle = { crumbs: string[] };

type Source = {
  routes: RouteObject[];
  basePath: string;
  namespace: keyof CustomTypeOptions['resources'];
  rootKey: string;
};

type Crumb = { label: string; path: string };

const RouteBreadcrumbsSource = createContext<Source | null>(null);

export const RouteBreadcrumbsProvider = ({ children, ...source }: PropsWithChildren<Source>) => (
  <RouteBreadcrumbsSource.Provider value={source}>{children}</RouteBreadcrumbsSource.Provider>
);

type Props = {
  /** Appends an extra crumb after the route's own crumbs, for ids and other params with no crumb of their own. */
  leaf?: string;
};

const RouteBreadcrumbs = ({ leaf }: Props) => {
  const source = useContext(RouteBreadcrumbsSource);
  const { pathname } = useLocation();
  const { t } = useTranslation(source?.namespace);
  // Handle keys are runtime strings, which the typed `t` cannot check.
  const translate = t as unknown as (key: string) => string;

  if (!source) {
    return null;
  }

  const matches = matchRoutes(source.routes, pathname.slice(source.basePath.length) || '/') ?? [];

  const crumbs: Crumb[] = matches.flatMap((match) => {
    const labels = (match.route.handle as CrumbHandle | undefined)?.crumbs ?? [];
    // Splat segments ('*') have no crumb of their own, so they must not shift the label indexes.
    const ownSegments = (match.route.path ?? '').split('/').filter((segment) => segment && segment !== '*');
    const matchedSegments = match.pathnameBase.split('/').filter(Boolean);
    const firstOwnIndex = matchedSegments.length - ownSegments.length;

    return ownSegments.flatMap((_, index) => {
      const label = labels[index];
      if (!label) {
        return [];
      }
      const path = `${source.basePath}/${matchedSegments.slice(0, firstOwnIndex + index + 1).join('/')}`;
      const text = label.startsWith(':') ? (match.params[label.slice(1)] ?? '') : translate(label);
      return [{ label: text, path }];
    });
  });

  if (leaf) {
    crumbs.push({ label: leaf, path: pathname });
  }

  if (crumbs.length === 0) {
    return null;
  }

  return (
    <Breadcrumbs>
      <Breadcrumbs.List>
        <Breadcrumbs.Item>
          <Breadcrumbs.Link asChild>
            <Link to={source.basePath}>{translate(source.rootKey)}</Link>
          </Breadcrumbs.Link>
        </Breadcrumbs.Item>
        {crumbs.map((crumb, index) => (
          <Breadcrumbs.Item key={crumb.path}>
            <Breadcrumbs.Link asChild aria-current={index === crumbs.length - 1 ? 'page' : undefined}>
              <Link to={crumb.path}>{crumb.label}</Link>
            </Breadcrumbs.Link>
          </Breadcrumbs.Item>
        ))}
      </Breadcrumbs.List>
    </Breadcrumbs>
  );
};

export default RouteBreadcrumbs;
